import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Empty, FormDialog, PageHead, StatusBadge, VEHICLE_TYPES, money, typeLabel, useApi, vehLabel } from "@/components/shop/common";

const CUSTOMER_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "company", label: "Company / fleet", half: true },
  { name: "phone", label: "Phone", half: true },
  { name: "email", label: "Email", type: "email" },
  { name: "address", label: "Address" },
  { name: "notes", label: "Notes", type: "textarea" },
];
const VEHICLE_FIELDS = [
  { name: "type", label: "Type", type: "select", options: VEHICLE_TYPES, required: true },
  { name: "year", label: "Year", half: true },
  { name: "unit_number", label: "Unit #", half: true },
  { name: "make", label: "Make", required: true, half: true },
  { name: "model", label: "Model", required: true, half: true },
  { name: "vin", label: "VIN / serial" },
  { name: "plate", label: "Plate", half: true },
  { name: "odometer", label: "Odometer / hours", half: true },
];

const Detail = ({ c, onEdit, onDelete }) => {
  const [vehicles, loadV] = useApi(`/vehicles?customer_id=${c.id}`);
  const [wos] = useApi(`/work-orders?customer_id=${c.id}`);
  const [vd, setVd] = useState(null);
  const saveV = async (v) => {
    if (vd.id) await api.put(`/vehicles/${vd.id}`, { ...v, customer_id: c.id }); else await api.post("/vehicles", { ...v, customer_id: c.id });
    toast.success("Vehicle saved"); loadV();
  };
  const delV = async (v) => { if (window.confirm(`Delete ${vehLabel(v)}?`)) { await api.delete(`/vehicles/${v.id}`).catch((e) => toast.error(errMsg(e))); loadV(); } };
  return (
    <div className="bg-card/90 border border-border rounded-sm p-6 space-y-6" data-testid="customer-detail">
      <div className="flex justify-between items-start gap-3">
        <div>
          <h2 className="font-display text-3xl uppercase" data-testid="customer-detail-name">{c.name}</h2>
          <p className="text-sm text-muted-foreground">{[c.company, c.phone, c.email].filter(Boolean).join(" · ")}</p>
          {c.notes && <p className="text-sm mt-2">{c.notes}</p>}
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} data-testid="customer-edit-btn"><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={onDelete} className="hover:text-red-400" data-testid="customer-delete-btn"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Vehicles & units</h3>
          <Button size="sm" variant="outline" className="rounded-sm hover:text-white" onClick={() => setVd({ type: "car" })} data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-1" /> Vehicle</Button>
        </div>
        {vehicles?.length === 0 && <p className="text-sm text-muted-foreground">No vehicles yet.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          {vehicles?.map((v) => (
            <div key={v.id} className="border border-border rounded-sm p-3 flex justify-between gap-2" data-testid={`vehicle-card-${v.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-sm flex items-center gap-2"><Truck className="h-4 w-4 text-primary shrink-0" /> {vehLabel(v)}</p>
                <p className="text-xs text-muted-foreground mt-1">{typeLabel(v.type)}{v.vin && ` · ${v.vin}`}{v.odometer && ` · ${v.odometer}`}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setVd(v)} className="text-muted-foreground hover:text-white" data-testid={`vehicle-edit-${v.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => delV(v)} className="text-muted-foreground hover:text-red-400" data-testid={`vehicle-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Repair history</h3>
          <Button asChild size="sm" className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="customer-new-wo-btn"><Link to={`/app/work-orders?new=1&customer=${c.id}`}><Plus className="h-4 w-4 mr-1" /> Work order</Link></Button>
        </div>
        {wos?.length === 0 && <p className="text-sm text-muted-foreground">No work orders yet.</p>}
        {wos?.map((w) => (
          <Link key={w.id} to={`/app/work-orders/${w.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border last:border-0 text-sm hover:text-primary" data-testid={`customer-wo-${w.id}`}>
            <span><span className="font-mono text-primary mr-2">#{w.number}</span>{w.vehicle_label} — {w.complaint.slice(0, 60)}</span>
            <span className="flex items-center gap-3"><StatusBadge status={w.status} /><span className="font-mono">{money(w.total)}</span></span>
          </Link>
        ))}
      </div>
      <FormDialog open={!!vd} onOpenChange={(o) => !o && setVd(null)} title={vd?.id ? "Edit vehicle" : "Add vehicle"} fields={VEHICLE_FIELDS} initial={vd} onSubmit={saveV} testid="vehicle-dialog" />
    </div>
  );
};

export default function Customers() {
  const [list, load] = useApi("/customers");
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const current = list?.find((c) => c.id === sel);
  const shown = (list || []).filter((c) => `${c.name} ${c.company} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));

  const save = async (v) => {
    if (edit.id) await api.put(`/customers/${edit.id}`, v);
    else { const { data } = await api.post("/customers", v); setSel(data.id); }
    toast.success("Customer saved"); load();
  };
  const del = async () => {
    if (!window.confirm(`Delete ${current.name}?`)) return;
    try { await api.delete(`/customers/${current.id}`); setSel(null); load(); toast.success("Customer deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHead eyebrow="CRM" title="Customers & units">
        <Button onClick={() => setEdit({})} className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="add-customer-btn"><Plus className="h-4 w-4 mr-1" /> Customer</Button>
      </PageHead>
      {list?.length === 0 ? <Empty testid="customers-empty">No customers yet. Add your first one.</Empty> : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="w-full h-10 px-3 rounded-sm bg-card border border-input text-sm" data-testid="customer-search-input" />
            <div className="border border-border rounded-sm bg-card/80 divide-y divide-border max-h-[70vh] overflow-y-auto" data-testid="customer-list">
              {shown.map((c) => (
                <button key={c.id} onClick={() => setSel(c.id)} data-testid={`customer-item-${c.id}`}
                  className={`w-full text-left px-4 py-3 transition-colors ${sel === c.id ? "bg-primary/15 border-l-2 border-primary" : "hover:bg-secondary/50"}`}>
                  <p className="font-semibold text-sm">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{[c.company, c.phone].filter(Boolean).join(" · ")}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            {current ? <Detail key={current.id} c={current} onEdit={() => setEdit(current)} onDelete={del} /> : <Empty testid="customer-none-selected">Select a customer to see vehicles and repair history.</Empty>}
          </div>
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit customer" : "New customer"} fields={CUSTOMER_FIELDS} initial={edit} onSubmit={save} testid="customer-dialog" />
    </>
  );
}
